import _ from 'lodash'
import React from 'react'
import Tooltip from '../Tooltip'
import Snackbar from '../Snackbar'
import { Box, Checkbox, Grid, MenuItem, Select } from '@mui/material'
import { renderLevel } from '../../../utils/alertUtils'
import { productConfig } from '../../../config/config-service'
import { CAREPLAN_VALUE_TYPES } from '../../../constants/carePlanValueTypes'
import { CARE_PLAN_NOTIFY_OPTIONS } from '../../../constants/constants'
import { ALERT_LEVEL_COLORS } from '../../../utils/baseAlertUtils'
import PublicIcon from '@mui/icons-material/Public'
import PersonIcon from '@mui/icons-material/Person'
import { type IProductConfig } from '../../../interfaces/product-config.interface'
import { type ICarePlanItem } from '../../../interfaces/careplan.interface'

export interface ICarePlanItemProps {
  updateCarePlan?: (item: ICarePlanItem) => void
  shouldRenderCondensed?: boolean
  subtype?: string
  isDefault?: boolean
}

interface ICarePlanItemState {
  itemData: ICarePlanItem | null
}

/*
 * Base React component for care plan items.
 * Read through this class to understand how care plan items are rendered!
 */
export default class CarePlanItem extends React.Component<ICarePlanItemProps, ICarePlanItemState> {
  type: string
  subtype: string
  config: IProductConfig
  snackbarOpen: boolean

  constructor (props: ICarePlanItemProps) {
    super(props)

    // Define type and subtype to let fetchItemData() automatically retrieve the care plan item data
    this.type = ''
    this.subtype = props.subtype as string
    this.config = productConfig()
    this.snackbarOpen = false

    // Or you can set state.itemData yourself in the component's UNSAFE_componentWillMount() method
    this.state = {
      itemData: null
    }
  }

  UNSAFE_componentWillMount (): void {
    this.fetchItemData(this.props)
  }

  UNSAFE_componentWillReceiveProps (nextProps): void {
    this.fetchItemData(nextProps)
  }

  // Retrieve the relevant care plan item
  fetchItemData (props): void {
    const hasMatchingSubtype = (item: ICarePlanItem): boolean => {
      if (!this.subtype) return true
      return this.subtype === item.subtype
    }

    const itemData = props.carePlanItems?.find(item => item.type === this.type && hasMatchingSubtype(item))
    this.setState({
      itemData
    })
  }

  hasTrigger (alertTrigger): boolean {
    return !!this.state.itemData?.alert_triggers?.find(x => x.type === alertTrigger)
  }

  // Render custom value
  getValueElement (getItemVal): JSX.Element {
    if (!getItemVal) return <></>
    return <strong>{getItemVal(this.state.itemData)}</strong>
  }

  // Get raw value
  getValue (getItemVal): number {
    return getItemVal(this.state.itemData)
  }

  // Render dropdown
  onInputElementChange = (updateItem) => {
    return (e) => {
      const currentItem = _.cloneDeep(_.omit({ ...this.state.itemData }, 'updatedTime'))
      const newItem = updateItem(currentItem, e.target.value)
      this.props.updateCarePlan?.(newItem)
    }
  }

  getSelectElement (options, getItemVal, updateItem, disabled = false): JSX.Element {
    const itemData = this.state.itemData as ICarePlanItem

    return (
      <Select
        size='small'
        className='care-plan-item__select'
        value={getItemVal(itemData)}
        onChange={this.onInputElementChange(updateItem)}
        disabled={itemData.disabled === 1 || disabled}
      >
        {options}
      </Select>
    )
  }

  getInputElement (min: number, max: number, step: number, getItemVal, updateItem, disabled = false): JSX.Element {
    const options = _.range(min, max + 1, step).map(v =>
      <MenuItem className='care-plan-item__select-item' key={v} value={v}>{v}</MenuItem>
    )

    return this.getSelectElement(options, getItemVal, updateItem, disabled)
  }

  getCustomInputElement (selectOptions, getItemVal, updateItem): JSX.Element {
    const options = selectOptions.map(v => {
      const display = v > 0 ? v : 'never'
      return <MenuItem className='care-plan-item__select-item' key={v} value={v}>{display}</MenuItem>
    })

    return this.getSelectElement(options, getItemVal, updateItem)
  }

  // Dropdown element for alert level/colors
  getLevelInput (): JSX.Element {
    const getItemVal = CAREPLAN_VALUE_TYPES.LEVEL.get((val) => Number(val))
    const updateItem = CAREPLAN_VALUE_TYPES.LEVEL.set(val => Number(val))

    const options: JSX.Element [] = []
    for (const [level, color] of Object.entries(ALERT_LEVEL_COLORS)) {
      options.push(<MenuItem key={level} value={level}>{color}</MenuItem>)
    }

    return this.getSelectElement(options, getItemVal, updateItem)
  }

  renderNotify (): JSX.Element {
    const itemData = this.state.itemData as ICarePlanItem
    const options = CARE_PLAN_NOTIFY_OPTIONS.map(item => <MenuItem className='care-plan-item__select-item' key={item.key} value={item.key}>{item?.value}</MenuItem>)

    return (
      <Select
        fullWidth
        size='small'
        data-testid='care-plan-item__notify-select-dropdown'
        className='care-plan-item__select'
        value={itemData.notify}
        onChange={this.handleChangeNotify}
        id={itemData.notify}
        disabled={this.state.itemData?.disabled === 1 || !this.config.carePlan.notifyEnabled}
      >
        {options}
      </Select>
    )
  }

  handleClickEnabled = (): void => {
    this.setState({
      itemData: { ...this.state.itemData as ICarePlanItem, disabled: this.state.itemData?.disabled ? 0 : 1 }
    }, () => {
      const currentItem = _.omit(this.state.itemData, 'updatedTime')
      const newItem: ICarePlanItem = _.set(currentItem, 'disabled', this.state.itemData?.disabled)
      this.props.updateCarePlan?.(newItem)
    })
  }

  handleChangeNotify = (event): void => {
    this.setState({
      itemData: { ...this.state.itemData as ICarePlanItem, notify: event.target.value }
    }, () => {
      const currentItem = _.omit(this.state.itemData, 'updatedTime')
      const newItem = _.set(currentItem, 'notify', this.state.itemData?.notify)
      this.props.updateCarePlan?.(newItem)
    })
  }

  renderEnabledCheckbox (): JSX.Element {
    const label = { inputProps: { 'aria-label': 'Enable/Disable care plan item', content: '"✔"' } }
    return (
      <Checkbox {...label} checked={this.state.itemData?.disabled === 0} onChange={this.handleClickEnabled} sx={{ padding: 0, '& .MuiSvgIcon-root': { fontSize: 25 } }} />
    )
  }

  // User-facing columns
  renderGoal (): JSX.Element {
    return <div />
  }

  renderClinicianTrigger (): JSX.Element {
    return <div className='care-plan-row__empty'>No alert</div>
  }

  renderIsDefault (): JSX.Element {
    let icon = <PublicIcon />
    let tooltipText = 'This care plan item matches the default group care plan settings.'

    if (!this.state.itemData?.default) {
      icon = <PersonIcon />
      tooltipText = 'This care plan item has been modified from the default group care plan settings. This setting will persist even if you make changes to the default care plan settings.'
    }

    return (
      <div className='care-plan-row__is-default'>
        <Tooltip tooltipId='care-plan-row__is-default__tooltip' icon={icon} content={tooltipText} />
      </div>
    )
  }

  renderCondensed (): JSX.Element {
    return <div>&middot;</div>
  }

  renderEnabled (): JSX.Element {
    return this.renderEnabledCheckbox()
  }

  renderLevel (): JSX.Element {
    const level = Number(this.state.itemData?.level)
    return (
      <Box sx={{ display: 'inline-flex' }}>
        {renderLevel(level, true)}
        <span className='alert-level-text'>{this.getLevelInput()}</span>
      </Box>
    )
  }

  render (): JSX.Element {
    const itemData = this.state.itemData
    if (!itemData) return <></>
    if (this.props.shouldRenderCondensed) return this.renderCondensed()

    if (itemData.updatedTime) this.snackbarOpen = true
    else this.snackbarOpen = false

    const carePlan = this.config.carePlan
    let alertColumnSize = this.props.isDefault ? 5 : 4
    if (!carePlan.canBeDisabled) alertColumnSize++
    if (!carePlan.hasLevel && !carePlan.hasGoal) alertColumnSize += 4

    let carePlanRowClasses = 'care-plan-row'
    if (itemData.disabled) carePlanRowClasses += ' disabled'
    const hasGoalOrLevel = carePlan.hasLevel === true || carePlan.hasGoal === true

    return (
      <Box sx={{ flexGrow: 1 }} className={carePlanRowClasses}>
        <Grid container spacing={3}>
          {!this.props.isDefault && <Grid item xs={1}> {this.renderIsDefault()} </Grid>}
          {carePlan.canBeDisabled && <Grid item xs={1}>{this.renderEnabled()} </Grid>}
          {hasGoalOrLevel && <Grid item xs={3}> {carePlan.hasLevel ? this.renderLevel() : this.renderGoal()} </Grid>}
          <Grid item xs={alertColumnSize} fontWeight={300}> {this.renderClinicianTrigger()} </Grid>
          <Grid item xs={3}> {this.renderNotify()} </Grid>
        </Grid>

        <Snackbar
          isOpen={this.snackbarOpen}
          dataKey='care-plan-item__success__snackbar'
          handleClose={() => { this.snackbarOpen = false }}
          severity='success'
          message='Care plan updated successfully'
        />
      </Box>
    )
  }
}
